// src/components/chat/HumanChatWindow.jsx
//
// Shared two-way chat UI for Patient↔Doctor, Patient↔Navigator, and
// Navigator↔Doctor threads. Used from all three portals — pure
// var(--token) throughout, no hex fallbacks, same convention as the
// converged doctor-portal pages (PREVIU_DESIGN_TOKENS.md), since a chat
// screen looking like a different product from the rest of the portal it's
// embedded in was the actual bug this cleanup fixes.
//
// Deliberately NOT built on top of ChatWindow.jsx (Ask Previu UI) —
// that component carries a lot of AI-specific chrome (tool status cards,
// welcome bubble, quick actions, widget message types) that doesn't apply
// here. This is a plain two-person message list + composer.

import React, { useEffect, useRef, useState } from 'react';
import avatarTint, { avatarAccentFill } from '../../utils/avatarTint';
import textLang from '../../utils/textLang';
import Icon from '../Icon';
import { useToast } from '../../context/ToastContext';

// Video / voice — Figma reference for this header. Neither actually places
// a call yet (there's no telephony/video integration behind this chat), so
// clicking one says so rather than doing nothing — a clickable-looking icon
// with no feedback reads as broken, not as "not built yet".
//
// A third "Message" icon used to sit here too, wired to the identical
// "isn't available yet" toast. That was actively wrong, not just an
// unbuilt placeholder: this whole screen IS the messaging interface, with
// a working composer right below it — a header button claiming messaging
// isn't available, sitting directly above the composer that proves it is,
// read as the feature being broken. Removed rather than pointed at
// anything, since there's nothing else for a "Message" header icon to do
// on a screen that already is the message thread.
const HEADER_ACTIONS = [
  { icon: 'video', label: 'Video call' },
  { icon: 'phone', label: 'Voice call' },
];

function formatTime(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

// Every bubble only ever showed formatTime() — fine within a single day, but
// a thread spanning more than one (the normal case for an ongoing care-team
// conversation, not just a stale/old one) left every message reading like it
// happened today, with no way to tell "12:34" this morning from "12:34"
// three days ago. Mirrors the date-separator convention ChatWindow.jsx (the
// Ask Previu) already uses for the same reason.
function formatDateLabel(iso) {
  const date = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return 'Today';
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return date.toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' });
}

function DateSeparator({ label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '4px 0' }}>
      <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
      <span style={{
        fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase',
        letterSpacing: '0.8px', fontFamily: 'var(--font-body)',
      }}>{label}</span>
      <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
    </div>
  );
}

function initials(name) {
  if (!name) return '?';
  return name.trim().split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase();
}

export default function HumanChatWindow({
  counterpartName,
  counterpartRole,   // 'PATIENT' | 'DOCTOR' | 'NAVIGATOR' — for the header subtitle
  counterpartId,     // used to infer which messages are "mine": a conversation only ever has 2 parties, so any sender that ISN'T the counterpart must be me — avoids needing to resolve "my own id" client-side, which is awkward for staff (JWT subject is email, not id)
  messages,
  loading,
  error,
  sending,
  onSend,
  onBack,            // optional — when provided, shows a "← Back" control (mobile only, via .chatinbox-back-btn CSS defined by the parent page) that returns to the thread list instead of showing both panes at once
}) {
  const [input, setInput] = useState('');
  const bottomRef = useRef(null);
  const textareaRef = useRef(null);
  const { toast } = useToast();
  // Each counterpart's own accent color — same one their avatar renders in
  // — carried through to their sender name and outgoing-bubble gradient, so
  // a thread with Dr. Priya reads visually distinct from one with Rakesh
  // rather than every conversation using the same static brand accent.
  const accentFill = avatarAccentFill(counterpartName);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  function handleSend() {
    const trimmed = input.trim();
    if (!trimmed || sending) return;
    onSend(trimmed);
    setInput('');
    textareaRef.current?.focus();
  }

  function handleKeyDown(e) {
    // A phonetic/IME keyboard (Malayalam, Hindi) uses Enter to commit the
    // word being composed — sending then would post a half-typed word.
    // keyCode 229 covers browsers that don't set isComposing on that keydown.
    if (e.nativeEvent.isComposing || e.keyCode === 229) return;
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  }

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, width: '100%',
      boxSizing: 'border-box', background: 'var(--bg-canvas)', borderRadius: 'var(--radius-md)',
      border: '1px solid var(--border)', overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '14px 18px', background: 'var(--brand-dark)',
        flexShrink: 0,
      }}>
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            aria-label="Back to conversation list"
            className="chatinbox-back-btn"
            style={{
              display: 'none', // shown only on mobile — see the media query each parent page defines
              border: 'none', background: 'none', color: 'var(--text-inverse)',
              fontSize: 20, cursor: 'pointer', padding: '0 4px 0 0', flexShrink: 0,
              alignItems: 'center', lineHeight: 1,
            }}
          >
            ←
          </button>
        )}
        <div className={`avatar ${avatarTint(counterpartName)}`} aria-hidden="true">
          {initials(counterpartName)}
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{
            fontFamily: 'var(--font-body)', fontSize: 14,
            fontWeight: 700, color: 'var(--text-on-brand-dark)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {counterpartName || 'Conversation'}
          </div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)' }}>
            {counterpartRole === 'DOCTOR' ? 'Doctor' : counterpartRole === 'NAVIGATOR' ? 'Navigator' : counterpartRole === 'PATIENT' ? 'Patient' : counterpartRole === 'SUBSCRIBER' ? 'Subscriber' : ''}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 6, marginLeft: 'auto', flexShrink: 0 }}>
          {HEADER_ACTIONS.map(({ icon, label }) => (
            <button
              key={icon}
              type="button"
              aria-label={label}
              title={label}
              onClick={() => toast.info(`${label} isn't available yet.`)}
              style={{
                width: 30, height: 30, borderRadius: '50%', border: 'none',
                background: 'rgba(255,255,255,0.12)', color: 'var(--text-on-brand-dark)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', flexShrink: 0, padding: 0,
              }}
            >
              <Icon name={icon} size={15} />
            </button>
          ))}
        </div>
      </div>

      {/* Message list */}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {/* Auto-margin spacer: pushes a short thread down so it sits just above
            the composer instead of stranding it at the top under a block of
            empty space. Collapses to nothing once the list overflows, so
            scrolling still behaves — unlike justify-content: flex-end, which
            clips the first messages when the content is taller than the box. */}
        <div style={{ marginTop: 'auto' }} />

        {loading && (
          <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 13, marginTop: 20 }}>
            Loading conversation…
          </div>
        )}

        {!loading && messages.length === 0 && (
          <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 13, marginTop: 20 }}>
            No messages yet — say hello.
          </div>
        )}

        {messages.map((m, i) => {
          const isMine = m.senderId !== counterpartId;
          const prevDay = i > 0 ? formatDateLabel(messages[i - 1].createdAt) : null;
          const thisDay = formatDateLabel(m.createdAt);
          const showDateSeparator = thisDay !== prevDay;
          return (
            <React.Fragment key={m.id}>
            {showDateSeparator && <DateSeparator label={thisDay} />}
            <div style={{ display: 'flex', minWidth: 0, justifyContent: isMine ? 'flex-end' : 'flex-start' }}>
              <div style={{
                maxWidth: '75%', minWidth: 0, padding: '9px 14px', borderRadius: isMine ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                // Softer tinted gradient, not the two flat stops this used to
                // be — accentFill blended into --brand-dark (always dark in
                // both themes, unlike --bg-canvas/--bg-surface which flip) so
                // the result reads as a quiet tint rather than a saturated
                // block, while staying safely dark enough for the always-white
                // --text-on-brand-dark text in either theme. The faint
                // matching border and whisper-soft shadow are the same idea —
                // just enough definition, without the bubble overpowering the
                // thread.
                background: isMine
                  ? `linear-gradient(135deg, color-mix(in srgb, ${accentFill} 55%, var(--brand-dark)), var(--brand-dark))`
                  : 'var(--bg-surface)',
                color: isMine ? 'var(--text-on-brand-dark)' : 'var(--text-primary)',
                border: isMine
                  ? `1px solid color-mix(in srgb, ${accentFill} 45%, var(--brand-dark))`
                  : '1px solid var(--border-strong)',
                boxShadow: isMine ? '0 1px 3px rgba(0,0,0,0.18)' : 'none',
                fontSize: 13.5, lineHeight: 1.5, fontFamily: 'var(--font-body)',
                boxSizing: 'border-box',
              }}>
                {!isMine && (
                  <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 3, color: accentFill }}>
                    {m.senderName}
                  </div>
                )}
                <div lang={textLang(m.body)} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.body}</div>
                <div style={{
                  fontSize: 10, marginTop: 4, textAlign: 'right',
                  // 0.6 -> 0.75: legible without drawing the eye off the
                  // message text itself.
                  color: isMine ? 'rgba(255,255,255,0.75)' : 'var(--text-muted)',
                }}>
                  {formatTime(m.createdAt)}
                </div>
              </div>
            </div>
            </React.Fragment>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Error banner */}
      {error && (
        <div style={{ padding: '8px 18px', background: 'var(--red-bg)', color: 'var(--red)', fontSize: 12, flexShrink: 0 }}>
          ⚠ {error}
        </div>
      )}

      {/* Composer — same .form-textarea every other text input in the app
          uses, not a bespoke pill shape, per the design-system convergence. */}
      <div style={{
        display: 'flex', gap: 8, padding: '12px 14px',
        borderTop: '1px solid var(--border)',
        background: 'var(--bg-surface)', flexShrink: 0,
      }}>
        <textarea
          ref={textareaRef}
          className="form-textarea"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={1}
          placeholder="Type a message…"
          aria-label="Type a message"
          style={{ flex: 1, resize: 'none', minHeight: 'auto', maxHeight: 100, overflowY: 'auto' }}
        />
        <button
          type="button"
          onClick={handleSend}
          disabled={!input.trim() || sending}
          aria-label="Send message"
          style={{
            width: 40, height: 40, borderRadius: '50%', border: 'none', flexShrink: 0,
            background: !input.trim() || sending ? 'var(--bg-subtle)' : 'var(--brand-accent)',
            color: !input.trim() || sending ? 'var(--text-muted)' : 'var(--text-inverse)',
            cursor: !input.trim() || sending ? 'not-allowed' : 'pointer',
            fontSize: 16,
          }}
        >
          {sending ? '⏳' : '➤'}
        </button>
      </div>
    </div>
  );
}
